import { createContext, useContext } from "react";

// ── API ──
const API = process.env.REACT_APP_API_URL || "/api";
export const getToken = () => localStorage.getItem("gc_token");
export const setToken = (t) => t ? localStorage.setItem("gc_token", t) : localStorage.removeItem("gc_token");

export const api = async (path, opts = {}) => {
  const token = getToken();
  const headers = { "Content-Type": "application/json", Accept: "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  try {
    const res = await fetch(`${API}${path}`, { ...opts, headers: { ...headers, ...opts.headers } });
    const data = await res.json().catch(() => null);
    if (!res.ok) return { ok: false, status: res.status, message: data?.message || "Something went wrong", data };
    return { ok: true, status: res.status, data, message: data?.message };
  } catch (e) {
    return { ok: false, status: 0, message: "Network error. Please try again." };
  }
};

export const timeAgo = (d) => {
  if (!d) return "";
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return "Just now";
  if (s < 3600) return Math.floor(s / 60) + "m ago";
  if (s < 86400) return Math.floor(s / 3600) + "h ago";
  if (s < 604800) return Math.floor(s / 86400) + "d ago";
  return new Date(d).toLocaleDateString("en-KE", { month: "short", day: "numeric" });
};

export const fixAvatarUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http") || url.startsWith("/")) return url;
  return `/storage/${url}`;
};

// ── CONTEXT ──
export const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

// ── CONSTANTS ──
export const EMOJI_CATS = {
  "😊 Smileys": ["😀","😃","😄","😁","😆","😅","🤣","😂","🙂","😊","😇","🥰","😍","🤩","😘","😗","😚","😙","🥲","😋","😛","😜","🤪","😝","🤗","🤭","🤫","🤔","😐","😏","😌","😔","😴","🤯","🥳","😎","🤓","🧐","🫡","😤","😠","🥺","😢","😭"],
  "❤️ Emotions": ["❤️","🧡","💛","💚","💙","💜","🖤","🤍","💔","❤️‍🔥","💕","💞","💓","💗","💖","💘","💝","😈","👿"],
  "👍 Gestures": ["👍","👎","👊","✊","🤛","🤜","👏","🙌","🫶","🤝","🙏","✌️","🤞","🤟","🤘","👌","🤌","👋","💪","🫰","🤙","👈","👉","☝️"],
  "🔥 Popular": ["🔥","💯","✨","⭐","🌟","💫","🎉","🎊","🏆","🥇","💎","🌈","☀️","🌙","🦋","🌺","🍀","🎭","🎨","🎵"],
  "🎁 Objects": ["💻","📱","🎧","📷","🎮","🕹️","💄","👗","👠","💍","💐","🧸","🎁","📚","✏️","🔑","💊","🛡️","🔒","💡"]
};

export const TOPICS = [
  { id: "general", name: "General", icon: "💬", color: "#6366f1" },
  { id: "trending", name: "Trending", icon: "🔥", color: "#ef4444" },
  { id: "relationships", name: "Relationships", icon: "💕", color: "#ec4899" },
  { id: "spicy", name: "Erotic", icon: "🔞", color: "#ef4444" },
];

export const BADGES = [
  { id: "b1", name: "First Post", icon: "📝", desc: "Published your first post", earned: false },
  { id: "b2", name: "Conversation Starter", icon: "💬", desc: "Started 10 conversations", earned: false },
  { id: "b3", name: "Trusted Member", icon: "⭐", desc: "Reached trust score 50", earned: false },
  { id: "b4", name: "Helpful", icon: "🤝", desc: "Received 50 likes", earned: false },
  { id: "b5", name: "Verified", icon: "✓", desc: "Completed identity verification", earned: false },
  { id: "b6", name: "Premium", icon: "👑", desc: "Upgraded to Premium", earned: false },
  { id: "b7", name: "Community Leader", icon: "🏆", desc: "Reached trust score 90", earned: false },
  { id: "b8", name: "1 Year", icon: "🎂", desc: "1 year in the community", earned: false },
];

export const darkColors = {
  bg: "#0b0c10", bg2: "#12131a", card: "#181a24", hover: "#1e2030",
  input: "#1e2030", border: "#252840", borderL: "#2d3054",
  text: "#e4e6f0", sub: "#8890b0", faint: "#505878",
  pri: "#7c6cff", priBg: "#7c6cff12", priBrd: "#7c6cff35", priTxt: "#a49bff",
  gold: "#f0b429", goldBg: "#f0b42912", goldTxt: "#f0b429",
  grn: "#34d399", grnBg: "#34d39912", grnTxt: "#34d399",
  red: "#f87171", redBg: "#f8717112", redTxt: "#f87171",
  blue: "#60a5fa", blueBg: "#60a5fa12",
  shd: "0 4px 24px rgba(0,0,0,0.5)", grad: "linear-gradient(135deg,#6c5ce7,#7c6cff)",
  grad2: "linear-gradient(135deg,#f0b429,#f59e0b)",
};

export const lightColors = {
  bg: "#f4f5f9", bg2: "#ecedf3", card: "#ffffff", hover: "#f8f9fc",
  input: "#f0f1f6", border: "#e0e2eb", borderL: "#ecedf3",
  text: "#1a1c2e", sub: "#6b7094", faint: "#9ca0b8",
  pri: "#5b4cdb", priBg: "#5b4cdb0d", priBrd: "#5b4cdb28", priTxt: "#5b4cdb",
  gold: "#c48a08", goldBg: "#c48a080d", goldTxt: "#c48a08",
  grn: "#059669", grnBg: "#0596690d", grnTxt: "#059669",
  red: "#dc2626", redBg: "#dc26260d", redTxt: "#dc2626",
  blue: "#2563eb", blueBg: "#2563eb0d",
  shd: "0 2px 12px rgba(0,0,0,0.06)", grad: "linear-gradient(135deg,#5b4cdb,#6c5ce7)",
  grad2: "linear-gradient(135deg,#c48a08,#f59e0b)",
};
